import { Trash2, Plus, FolderPlus } from "lucide-react";
import type {
  InventoryRule,
  InventoryRuleField,
  InventoryRuleGroup,
  InventoryRuleOperator,
} from "@lumika/shared";
import {
  Button,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@lumika/ui";

/**
 * Operator-facing editor for an `InventoryRuleGroup`.
 *
 * A group is `combinator: "all" | "any"` over a list of leaf rules
 * and nested sub-groups (recursive). Leaf rules use field
 * `rooftop|condition|make|fuel`, operators
 * `equals|not_equals|contains|not_contains|in|not_in`, and the value
 * input switches to a comma-separated tag input for in/not_in.
 *
 * Null value (no rules) is the operator's "match every row" intent
 * — the rule compiler short-circuits to `() => true` on either null
 * or an empty group, so the editor's empty state is meaningful.
 *
 * Depth is capped at MAX_DEPTH so a careless operator can't nest the
 * editor into a render loop. The DTO itself does not enforce a cap;
 * the cap is purely a UX guardrail.
 */

const MAX_DEPTH = 3;

const FIELDS: { value: InventoryRuleField; label: string }[] = [
  { value: "rooftop", label: "Rooftop (from CSV)" },
  { value: "condition", label: "Condition" },
  { value: "make", label: "Make" },
  { value: "fuel", label: "Fuel" },
];

const OPERATORS: { value: InventoryRuleOperator; label: string }[] = [
  { value: "equals", label: "equals" },
  { value: "not_equals", label: "does not equal" },
  { value: "contains", label: "contains" },
  { value: "not_contains", label: "does not contain" },
  { value: "in", label: "is one of" },
  { value: "not_in", label: "is not one of" },
];

const MULTI_OPS = new Set<InventoryRuleOperator>(["in", "not_in"]);

export function InventoryImportRuleEditor({
  value,
  onChange,
  disabled,
}: {
  value: InventoryRuleGroup | null;
  onChange: (next: InventoryRuleGroup | null) => void;
  disabled?: boolean;
}) {
  // Empty group ≡ null for the consumer — collapse to null on output
  // so the API sees a clear "no filter" rather than `{combinator,
  // rules:[]}`. The editor itself works with a defaulted group state.
  const group: InventoryRuleGroup = value ?? { combinator: "all", rules: [] };

  return (
    <GroupEditor
      group={group}
      depth={0}
      disabled={disabled}
      onChange={(next) =>
        onChange(next.rules.length === 0 ? null : next)
      }
    />
  );
}

function GroupEditor({
  group,
  depth,
  disabled,
  onChange,
  onRemove,
}: {
  group: InventoryRuleGroup;
  depth: number;
  disabled?: boolean;
  onChange: (next: InventoryRuleGroup) => void;
  /** When provided, the group can be deleted (sub-groups only). */
  onRemove?: () => void;
}) {
  const setCombinator = (combinator: "all" | "any") =>
    onChange({ ...group, combinator });
  const setNode = (idx: number, node: InventoryRule | InventoryRuleGroup) =>
    onChange({
      ...group,
      rules: group.rules.map((r, i) => (i === idx ? node : r)),
    });
  const removeNode = (idx: number) =>
    onChange({ ...group, rules: group.rules.filter((_, i) => i !== idx) });
  const addRule = () =>
    onChange({
      ...group,
      rules: [
        ...group.rules,
        { field: "make", operator: "equals", value: "" } satisfies InventoryRule,
      ],
    });
  const addGroup = () =>
    onChange({
      ...group,
      rules: [
        ...group.rules,
        { combinator: "all", rules: [] } satisfies InventoryRuleGroup,
      ],
    });

  if (group.rules.length === 0 && depth === 0) {
    return (
      <div className="flex flex-col items-start gap-2 rounded-md border border-dashed bg-muted/30 p-3">
        <p className="text-sm text-muted-foreground">
          No rules yet — every parsed row passes through to the rooftop.
          Add a rule (a single condition) or a group (a nested AND/OR
          set of conditions).
        </p>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={addRule}
            disabled={disabled}
            type="button"
          >
            <Plus className="h-4 w-4" />
            Add rule
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={addGroup}
            disabled={disabled}
            type="button"
          >
            <FolderPlus className="h-4 w-4" />
            Add group
          </Button>
        </div>
      </div>
    );
  }

  const canNestDeeper = depth < MAX_DEPTH - 1;
  // The combinator picker drives whether the rules in this group are
  // AND'd or OR'd together. We surface the boolean explicitly in the
  // option labels — "ALL" alone is jargon; "ALL (AND)" is not.
  const verb = group.combinator === "all" ? "AND" : "OR";

  return (
    <div
      className={[
        "flex flex-col gap-3 rounded-md border p-3",
        depth === 0 ? "bg-muted/30" : "bg-background",
      ].join(" ")}
    >
      <div className="flex items-center gap-2 text-sm">
        <span className="text-muted-foreground">A row matches when</span>
        <div className="w-40">
          <Select
            value={group.combinator}
            onValueChange={(v) => setCombinator(v as "all" | "any")}
            disabled={disabled}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">ALL (AND)</SelectItem>
              <SelectItem value="any">ANY (OR)</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <span className="text-muted-foreground">
          of the following are true — joined by <strong className="font-mono">{verb}</strong>:
        </span>
        {onRemove && (
          <Button
            variant="ghost"
            size="icon"
            onClick={onRemove}
            aria-label="Remove group"
            disabled={disabled}
            type="button"
            className="ml-auto"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        )}
      </div>

      <ul className="flex flex-col gap-2">
        {group.rules.map((node, idx) => (
          <li key={idx} className="flex flex-col gap-2">
            {/* Connector pill between siblings makes the AND/OR
                semantics impossible to miss visually (no need to
                re-read the group header). */}
            {idx > 0 && (
              <div className="flex items-center gap-2 px-1">
                <span className="h-px flex-1 bg-border" aria-hidden />
                <span className="rounded-full border bg-background px-2 py-0.5 font-mono text-[10px] uppercase tracking-wide text-muted-foreground">
                  {verb}
                </span>
                <span className="h-px flex-1 bg-border" aria-hidden />
              </div>
            )}
            {"combinator" in node ? (
              <GroupEditor
                group={node}
                depth={depth + 1}
                disabled={disabled}
                onChange={(next) => setNode(idx, next)}
                onRemove={() => removeNode(idx)}
              />
            ) : (
              <RuleEditor
                rule={node}
                onChange={(next) => setNode(idx, next)}
                onRemove={() => removeNode(idx)}
                disabled={disabled}
              />
            )}
          </li>
        ))}
      </ul>

      <div className="flex flex-wrap gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={addRule}
          disabled={disabled}
          type="button"
        >
          <Plus className="h-4 w-4" />
          Add rule
        </Button>
        {canNestDeeper && (
          <Button
            variant="outline"
            size="sm"
            onClick={addGroup}
            disabled={disabled}
            type="button"
          >
            <FolderPlus className="h-4 w-4" />
            Add group
          </Button>
        )}
      </div>
    </div>
  );
}

function RuleEditor({
  rule,
  onChange,
  onRemove,
  disabled,
}: {
  rule: InventoryRule;
  onChange: (next: InventoryRule) => void;
  onRemove: () => void;
  disabled?: boolean;
}) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <div className="w-44">
        <Select
          value={rule.field}
          onValueChange={(v) =>
            onChange({ ...rule, field: v as InventoryRuleField })
          }
          disabled={disabled}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {FIELDS.map((f) => (
              <SelectItem key={f.value} value={f.value}>
                {f.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="w-44">
        <Select
          value={rule.operator}
          onValueChange={(v) => {
            const op = v as InventoryRuleOperator;
            onChange({
              ...rule,
              operator: op,
              // Shape the value to the operator: arrays for in/not_in,
              // strings for the rest.
              value: MULTI_OPS.has(op)
                ? Array.isArray(rule.value)
                  ? rule.value
                  : rule.value.length
                    ? [rule.value]
                    : []
                : Array.isArray(rule.value)
                  ? (rule.value[0] ?? "")
                  : rule.value,
            });
          }}
          disabled={disabled}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {OPERATORS.map((o) => (
              <SelectItem key={o.value} value={o.value}>
                {o.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="flex-1 min-w-[14rem]">
        {MULTI_OPS.has(rule.operator) ? (
          <Input
            placeholder="Comma-separated values"
            value={Array.isArray(rule.value) ? rule.value.join(", ") : rule.value}
            onChange={(e) =>
              onChange({
                ...rule,
                value: e.target.value
                  .split(",")
                  .map((s) => s.trim())
                  .filter(Boolean),
              })
            }
            disabled={disabled}
          />
        ) : (
          <Input
            placeholder="Value"
            value={Array.isArray(rule.value) ? (rule.value[0] ?? "") : rule.value}
            onChange={(e) => onChange({ ...rule, value: e.target.value })}
            disabled={disabled}
          />
        )}
      </div>
      <Button
        variant="ghost"
        size="icon"
        onClick={onRemove}
        aria-label="Remove rule"
        disabled={disabled}
        type="button"
      >
        <Trash2 className="h-4 w-4" />
      </Button>
    </div>
  );
}
